import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { proceduresApi, type ReservationPlan } from '@/api/proceduresApi'
import { ScheduleReserveDialog } from '@/components/procedures/ScheduleReserveDialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useInvalidateProcedureBoard } from '@/hooks/useProcedureBoard'
import { useInvalidateRunReservations } from '@/hooks/useProcessSteps'
import type { BoardRun } from '@/lib/board/boardRuns'
import { fromLocalInput } from '@/lib/planningDates'
import { localTodayISO } from '@/lib/procedureDates'
import { requestErrorMessage } from '@/lib/requestErrorMessage'
import { reserveOutcomeError, reservedAnything } from '@/lib/reserveOutcome'

/**
 * Reserve a run's inventory and equipment from the board.
 *
 * The same flow the procedures table offers after a schedule is saved: preview
 * the plan, then book it (partial by design). A reservation is timed from the
 * run's scheduled start, so a run with none first asks for one and saves it on
 * the run. Booking against a date the run does not carry would leave holds the
 * next schedule change silently releases.
 */
export function BoardReserveFlow({ run, onClose }: { run: BoardRun | null; onClose: () => void }) {
  const invalidateBoard = useInvalidateProcedureBoard()
  const invalidateRunReservations = useInvalidateRunReservations()
  const [stage, setStage] = useState<'date' | 'plan' | null>(null)
  const [dateValue, setDateValue] = useState('')
  const [dateSaving, setDateSaving] = useState(false)
  const [dateError, setDateError] = useState<string | null>(null)
  const [startsAt, setStartsAt] = useState('')
  const [plan, setPlan] = useState<ReservationPlan | null>(null)
  const [planLoading, setPlanLoading] = useState(false)
  const [planBusy, setPlanBusy] = useState(false)
  const [planError, setPlanError] = useState<string | null>(null)

  const runId = run?.procedure.id ?? null
  const name = run?.procedure.procedureTemplate.name ?? 'This run'
  // The run the dialog is currently for. A plan that arrives after the dialog
  // moved to another run (or closed) is dropped instead of shown there.
  const activeRunId = useRef<string | null>(null)
  activeRunId.current = runId

  async function openPlan(id: string, iso: string) {
    setStage('plan')
    setStartsAt(iso)
    setPlan(null)
    setPlanError(null)
    setPlanLoading(true)
    try {
      const next = await proceduresApi.reservationPlan(id, iso)
      if (activeRunId.current === id) setPlan(next)
    } catch (e) {
      if (activeRunId.current === id) setPlanError(requestErrorMessage(e, 'Could not work out what this run needs.'))
    } finally {
      if (activeRunId.current === id) setPlanLoading(false)
    }
  }

  // Keyed on the run id: a board refetch hands over a new object for the same
  // run, which must not restart the flow.
  useEffect(() => {
    if (!run) {
      setStage(null)
      return
    }
    if (run.procedure.scheduledDate) {
      void openPlan(run.procedure.id, run.procedure.scheduledDate)
    } else {
      setDateValue('')
      setDateError(null)
      setStage('date')
    }
  }, [runId])

  async function saveDate() {
    if (!run) return
    const iso = fromLocalInput(dateValue)
    if (!iso) {
      setDateError('Choose a date and time.')
      return
    }
    setDateSaving(true)
    setDateError(null)
    try {
      await proceduresApi.update(run.procedure.id, { scheduledDate: iso })
      void invalidateBoard()
      await openPlan(run.procedure.id, iso)
    } catch (e) {
      setDateError(requestErrorMessage(e, 'Failed to set the scheduled start.'))
    } finally {
      setDateSaving(false)
    }
  }

  async function confirm() {
    if (!run) return
    setPlanBusy(true)
    setPlanError(null)
    try {
      const res = await proceduresApi.reserveForSchedule(run.procedure.id, startsAt)
      if (reservedAnything(res)) {
        invalidateRunReservations()
        void invalidateBoard()
      }
      const outcomeError = reserveOutcomeError(res)
      if (outcomeError) {
        setPlanError(outcomeError)
        return
      }
      onClose()
    } catch (e) {
      setPlanError(requestErrorMessage(e, 'Could not reserve for this run.'))
    } finally {
      setPlanBusy(false)
    }
  }

  return (
    <>
      <Dialog open={stage === 'date'} onOpenChange={open => !open && onClose()}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Set a scheduled start</DialogTitle>
            <DialogDescription>
              {name} has no scheduled start. Reservations are timed from it, so set one first.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <label className='text-sm font-medium' htmlFor='board-scheduled-start'>
              Scheduled start
            </label>
            <Input
              id='board-scheduled-start'
              type='datetime-local'
              value={dateValue}
              min={`${localTodayISO()}T00:00`}
              onChange={e => setDateValue(e.target.value)}
            />
            {dateError && (
              <p className='flex items-center gap-1.5 text-xs text-destructive'>
                <AlertCircle className='h-3.5 w-3.5 shrink-0' />
                {dateError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={saveDate} disabled={!dateValue || dateSaving}>
              {dateSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduleReserveDialog
        open={stage === 'plan'}
        procedureName={name}
        plan={plan}
        loading={planLoading}
        busy={planBusy}
        error={planError}
        onConfirm={confirm}
        onSkip={onClose}
      />
    </>
  )
}
